import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Container, Paper, Tab, Tabs } from "@mui/material";
import {
  AccountCircle,
  Lock,
  Edit,
  VpnKey,
  Home,
  Subscriptions,
  SubscriptionsOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyTradePreference,
  fetchUserProfile,
  saveTradePreference,
  updateUserProfile,
} from "../../../features/user/userSlice";
import { showAppMessageToast } from "../../../utils/toastUtils";
import Loader from "../../common/Loader";
import ProfileTab from "./ProfileTab";
import EditProfileTab from "./EditProfileTab";
import KycVerificationTab from "./KycVerificationTab";
import ChangePasswordTab from "./ChangePasswordTab";
import MyAddressesTab from "./MyAddressesTab";
import TabPanel from "./sampleData";
import { customTheme } from "./customTheme";
import theme from "../../../styles/theme/theme";
import SubscriptionBillingTab from "./SubscriptionBillingTab";

const ProfilePage = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const { profile, profileLoading, myPreference } = useSelector(
    (state) => state.user,
  );

  // console.log('Profile from Redux:', profile);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await dispatch(fetchUserProfile()).unwrap();
        if (res?.kycStatus === "verified") {
          dispatch({ type: "ui/clearKycWarning" });
        }
        await dispatch(fetchMyTradePreference()).unwrap();
      } catch (err) {
        showAppMessageToast({
          type: "error",
          title: "Failed to Load Profile",
          message: err || "Something went wrong!",
        });
      }
    };
    fetchData();
  }, [dispatch]);

  const handleSave = async (values, { setSubmitting }) => {
    setSaving(true);
    try {
      const { countryCode, phone, trade_preferences, profile_image, ...rest } =
        values;

      const payload = {
        ...values,
        phone: `${countryCode}${phone}`,
        trade_preferences: "",
      };

      // profile_image now already a File from EditProfileTab state
      if (values.profile_image) {
        payload.profile_image = values.profile_image;
      }

      await dispatch(updateUserProfile(payload)).unwrap();

      if (values.trade_preferences?.length > 0) {
        await dispatch(
          saveTradePreference({ preferences: values.trade_preferences }),
        ).unwrap();
      }

      await Promise.all([
        dispatch(fetchUserProfile()).unwrap(),
        dispatch(fetchMyTradePreference()).unwrap(),
      ]);

      setValue(0); // Go back to Profile tab

      showAppMessageToast({
        type: "success",
        title: "Profile Updated",
        message: "Changes saved successfully.",
      });
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Update Failed",
        message: err?.message || "Something went wrong!",
      });
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // const isLoading =
  //   profileLoading ||
  //   saving ||
  //   !profile ||
  //   !myPreference ||
  //   (Array.isArray(myPreference) && myPreference.length === 0);

  const isLoading = profileLoading || saving || !profile;

  return (
    <ThemeProvider theme={customTheme}>
      <Container
        maxWidth="lg"
        sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: "16px",
            minHeight: "60vh", //  keeps the card height consistent
            display: "flex",
            flexDirection: "column",
            // Glassy gradient
            background: "rgba(255, 255, 255, 0.15)", // semi-transparent
            backdropFilter: "blur(12px) saturate(180%)",
            WebkitBackdropFilter: "blur(12px) saturate(180%)", // Safari support
            border: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          {isLoading ? (
            <div
              style={{
                padding: theme.spacing(5),
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader />
            </div>
          ) : (
            <>
              {/* Tabs Wrapper */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: { xs: 2, md: 3 },
                  p: { xs: 1, md: 2 },
                  borderRadius: "16px",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                }}
              >
                <Tabs
                  value={value}
                  onChange={handleChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  aria-label="profile tabs"
                  sx={{
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: "bold",
                      borderRadius: "10px",
                      minHeight: "48px",
                      padding: { xs: "6px 8px", sm: "6px 12px" },
                      mr: { xs: 0.5, sm: 1 },
                      fontSize: { xs: "0.75rem", sm: "0.9rem" },
                      "&.Mui-selected": {
                        color: customTheme.palette.scraptradeDark.main,
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                      },
                      "&:hover": {
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.dark,
                        color: customTheme.palette.scraptradeDark.main,
                      },
                      "& .MuiTab-iconWrapper": {
                        mb: { xs: 0.5, sm: 0 },
                        mr: { xs: 0, sm: 1 },
                      },
                    },
                  }}
                >
                  <Tab
                    icon={<AccountCircle />}
                    iconPosition="start"
                    label="Profile"
                  />
                  <Tab
                    icon={<Edit />}
                    iconPosition="start"
                    label="Edit Profile"
                  />
                  {/* <Tab 
                    icon={<SubscriptionsOutlined />}
                    iconPosition="start"
                    label="Subscriptions & Billing"
                  /> */}
                  <Tab
                    icon={<VpnKey />}
                    iconPosition="start"
                    label="Verify Profile"
                  />
                  <Tab
                    icon={<Lock />}
                    iconPosition="start"
                    label="Change Password"
                  />
                  {/* <Tab
                    icon={<Home />}
                    iconPosition="start"
                    label="My Addresses"
                  /> */}
                </Tabs>
              </Box>

              {/* Tab Content */}
              <TabPanel value={value} index={0}>
                <ProfileTab user={profile} tradePreference={myPreference} />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <EditProfileTab
                  user={profile}
                  tradePreference={myPreference}
                  onSave={handleSave}
                />
              </TabPanel>
              {/* <TabPanel value={value} index={2}>
                <SubscriptionBillingTab  user={profile} />
              </TabPanel> */}
              <TabPanel value={value} index={2}>
                <KycVerificationTab />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <ChangePasswordTab />
              </TabPanel>
              {/* <TabPanel value={value} index={4}>
                <MyAddressesTab />
              </TabPanel> */}
            </>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ProfilePage;
